import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { roleAPI, userAPI } from '../services/api';
import { ArrowLeft, Search, ShieldCheck, Zap, ShieldOff, Plus, Trash2 } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import RolesMobile from '../components/users/RolesMobile';
import { useIsAppView } from '../components/users/kit';

// Builds an empty { view:false, add:false, ... } rights object for one module,
// restricted to the actions that module actually declares (see moduleRegistry.js).
// Actions are {key,label} objects, not bare strings — every module can have its own
// specific action set now (2026-07-26 granular-permissions rework), not a shared
// view/add/edit/delete vocabulary.
function emptyModuleRights(mod) {
  return Object.fromEntries(mod.actions.map(a => [a.key, false]));
}
function fullModuleRights(mod) {
  return Object.fromEntries(mod.actions.map(a => [a.key, true]));
}

const NEW_ROLE = { _id: null, name: '', description: '', rights: {} };

function RoleEditor({ role, registry, onSaved, onDeleted }) {
  const isNew = !role._id;
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description || '');
  const [rights, setRights] = useState(() => {
    const base = {};
    for (const mod of registry) base[mod.key] = { ...emptyModuleRights(mod), ...(role.rights?.[mod.key] || {}) };
    return base;
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(role.name);
    setDescription(role.description || '');
    const base = {};
    for (const mod of registry) base[mod.key] = { ...emptyModuleRights(mod), ...(role.rights?.[mod.key] || {}) };
    setRights(base);
  }, [role._id, registry]);

  const sections = useMemo(() => {
    const bySection = {};
    // FIELD AGENT APP is per-agent now (Agent Detail page's Field Collection Rights),
    // not resolved off a shared Site Role — see User.fieldRights and
    // middleware/auth.js's hasModuleRight. Showing it here would let an admin tick a
    // box that silently does nothing.
    for (const mod of registry) {
      if (mod.section === 'FIELD AGENT APP') continue;
      if (!bySection[mod.section]) bySection[mod.section] = [];
      bySection[mod.section].push(mod);
    }
    return bySection;
  }, [registry]);

  const toggle = (moduleKey, action) => {
    setRights(r => ({ ...r, [moduleKey]: { ...r[moduleKey], [action]: !r[moduleKey][action] } }));
  };
  const toggleRow = (mod, value) => {
    setRights(r => ({ ...r, [mod.key]: value ? fullModuleRights(mod) : emptyModuleRights(mod) }));
  };
  const grantAll = () => {
    const next = {};
    for (const mod of registry) next[mod.key] = fullModuleRights(mod);
    setRights(next);
  };
  const revokeAll = () => {
    const next = {};
    for (const mod of registry) next[mod.key] = emptyModuleRights(mod);
    setRights(next);
  };

  const save = async () => {
    if (!name.trim()) return toast.error('Role name is required');
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await roleAPI.create({ name: name.trim(), description, rights });
        toast.success(`Role "${name.trim()}" created`);
        onSaved(data.data);
      } else {
        const { data } = await roleAPI.update(role._id, { name: name.trim(), description, rights });
        toast.success(`Role "${name.trim()}" updated`);
        onSaved(data.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete role "${role.name}"? Users assigned to it will need a new role.`)) return;
    try {
      await roleAPI.delete(role._id);
      toast.success(`Role "${role.name}" deleted`);
      onDeleted(role._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete role');
    }
  };

  return (
    <div className="card">
      <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="flex items-center gap-3" style={{ flexWrap: 'wrap' }}>
          <input
            className="form-control" placeholder="Role name (e.g. Field Collection Agent)"
            value={name} onChange={e => setName(e.target.value)} style={{ maxWidth: 260, fontWeight: 600 }}
          />
          <input
            className="form-control" placeholder="Description (optional)"
            value={description} onChange={e => setDescription(e.target.value)} style={{ maxWidth: 320 }}
          />
        </div>
        <div className="flex items-center gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={revokeAll} title="Uncheck every right">
            <ShieldOff size={13} /> Revoke all
          </button>
          <button className="btn btn-secondary btn-sm" onClick={grantAll} title="Check every right">
            <Zap size={13} /> Grant full access
          </button>
          {!isNew && (
            <button className="btn btn-danger btn-sm" onClick={remove} title="Delete this role">
              <Trash2 size={13} /> Delete
            </button>
          )}
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : isNew ? 'Create Role' : 'Save Role'}
          </button>
        </div>
      </div>
      <div className="card-body" style={{ overflowX: 'auto' }}>
        {Object.entries(sections).map(([section, mods]) => (
          <div key={section} style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: '.72rem', fontWeight: 700, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8,
            }}>
              {section}
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {mods.map(mod => {
                const rowFull = mod.actions.every(a => rights[mod.key]?.[a.key]);
                return (
                  <div key={mod.key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 600, marginBottom: 8 }}>
                      <input type="checkbox" checked={rowFull} onChange={e => toggleRow(mod, e.target.checked)} />
                      {mod.label}
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', paddingLeft: 22 }}>
                      {mod.actions.map(action => (
                        <label key={action.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.82rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          <input type="checkbox" checked={!!rights[mod.key]?.[action.key]} onChange={() => toggle(mod.key, action.key)} />
                          {action.label}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SiteRolesPage() {
  return useIsAppView() ? <RolesMobile /> : <SiteRolesDesktop />;
}

function SiteRolesDesktop() {
  useDarkReady();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(undefined); // undefined = nothing picked yet, null = "new role" mode

  const load = async () => {
    setLoading(true);
    try {
      const [rolesRes, registryRes] = await Promise.all([roleAPI.getAll(), userAPI.getModuleRegistry()]);
      setRoles(rolesRes.data.data || []);
      setRegistry(registryRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load Site Roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = roles.filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()));
  const selected = selectedId === null ? NEW_ROLE : roles.find(r => r._id === selectedId) || null;

  const onSaved = (saved) => {
    setRoles(prev => {
      const exists = prev.some(r => r._id === saved._id);
      return exists ? prev.map(r => (r._id === saved._id ? saved : r)) : [...prev, saved];
    });
    setSelectedId(saved._id);
  };
  const onDeleted = (id) => {
    setRoles(prev => prev.filter(r => r._id !== id));
    setSelectedId(undefined);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/users')}>
          <ArrowLeft size={14} />
        </button>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
            <ShieldCheck size={16} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--primary)' }} />
            Site Roles
          </h2>
          <p className="text-muted text-small" style={{ margin: 0 }}>
            Create reusable rights templates — assign one to each staff, agent, or technical user from the Users page.
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, alignItems: 'start' }}>
        <div className="card">
          <div className="card-body" style={{ padding: 10 }}>
            <button className="btn btn-primary btn-sm" style={{ width: '100%', marginBottom: 10 }} onClick={() => setSelectedId(null)}>
              <Plus size={13} /> New Role
            </button>
            <div className="search-input-wrapper" style={{ marginBottom: 10 }}>
              <Search size={13} className="search-icon" />
              <input className="search-input" placeholder="Search roles…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            ) : filtered.length === 0 ? (
              <div className="text-muted text-small" style={{ padding: 10 }}>No Site Roles yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: 4 }}>
                {filtered.map(r => (
                  <button
                    key={r._id}
                    type="button"
                    onClick={() => setSelectedId(r._id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                      textAlign: 'left', border: 'none', borderRadius: 8, cursor: 'pointer',
                      padding: '8px 10px', background: r._id === selectedId ? 'var(--bg)' : 'transparent',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                      {r.description && <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{r.description}</div>}
                    </div>
                    <span className="badge badge-info" style={{ flexShrink: 0, fontSize: '.65rem' }}>{r.userCount} user{r.userCount === 1 ? '' : 's'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {selected && registry.length > 0 ? (
          <RoleEditor key={selected._id || 'new'} role={selected} registry={registry} onSaved={onSaved} onDeleted={onDeleted} />
        ) : (
          <div className="card">
            <div className="card-body text-muted" style={{ textAlign: 'center', padding: 40 }}>
              {loading ? 'Loading…' : 'Select a role on the left, or create a new one.'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
